/*
 * inochi-tracker.js — first-party traffic attribution for the Inochi storefront.
 *
 * On every page it mints/reads a persistent first-party visitor id and a 30-min
 * sliding session id, captures the marketing source (UTMs, referrer, ad click
 * IDs), permanently records FIRST-touch and refreshes LAST-touch, and beacons a
 * pageview to the `track` Supabase edge function (which does channel
 * classification server-side). It exposes window.InochiTracker.getAttribution()
 * so the membership/complimentary forms can stitch attribution onto an
 * application. No third-party pixels, no cross-site cookies.
 */
(function () {
  "use strict";

  var TRACK_URL = "https://ywjeykuqfybbekpdieip.supabase.co/functions/v1/track";
  var VID_KEY = "inochi_vid";        // persistent visitor id
  var SESS_KEY = "inochi_sess";      // { id, last } sliding session
  var FT_KEY = "inochi_ft";          // first-touch snapshot (immutable)
  var LT_KEY = "inochi_lt";          // last-touch snapshot (refreshed on new source)
  var SESSION_MS = 30 * 60 * 1000;   // 30 minutes idle → new session
  var VID_DAYS = 730;                // ~2 years
  var OWN_HOSTS = ["inochiteahouse.com", "wh0umw-6t.myshopify.com", "member.inochiteahouse.com"];

  // ── storage helpers (degrade gracefully if localStorage is unavailable) ──
  function lsGet(k) { try { return window.localStorage.getItem(k); } catch (e) { return null; } }
  function lsSet(k, v) { try { window.localStorage.setItem(k, v); } catch (e) {} }

  function setCookie(name, value, days) {
    var d = new Date();
    d.setTime(d.getTime() + days * 864e5);
    var secure = location.protocol === "https:" ? "; Secure" : "";
    document.cookie = name + "=" + encodeURIComponent(value) +
      "; expires=" + d.toUTCString() + "; path=/; SameSite=Lax" + secure;
  }
  function getCookie(name) {
    var m = document.cookie.match("(?:^|; )" + name + "=([^;]*)");
    return m ? decodeURIComponent(m[1]) : null;
  }

  function uuid() {
    try { if (window.crypto && crypto.randomUUID) return crypto.randomUUID(); } catch (e) {}
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
      var r = (Math.random() * 16) | 0;
      return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
    });
  }

  function ownHost(host) {
    if (!host) return false;
    host = host.toLowerCase().replace(/^www\./, "");
    for (var i = 0; i < OWN_HOSTS.length; i++) {
      if (host === OWN_HOSTS[i] || host.slice(-(OWN_HOSTS[i].length + 1)) === "." + OWN_HOSTS[i]) return true;
    }
    return false;
  }

  function refHost(ref) {
    if (!ref) return null;
    try { return new URL(ref).hostname.toLowerCase().replace(/^www\./, ""); } catch (e) { return null; }
  }

  // ── visitor id: cookie ↔ localStorage mirror, minted once ──
  var isNewVisitor = false;
  function getVisitorId() {
    var id = getCookie(VID_KEY) || lsGet(VID_KEY);
    if (!id) { id = uuid(); isNewVisitor = true; }
    setCookie(VID_KEY, id, VID_DAYS);
    lsSet(VID_KEY, id);
    return id;
  }

  // ── session id: 30-min sliding window ──
  var isNewSession = false;
  function getSessionId() {
    var now = Date.now();
    var raw = lsGet(SESS_KEY);
    var sess = null;
    if (raw) { try { sess = JSON.parse(raw); } catch (e) { sess = null; } }
    if (!sess || !sess.id || (now - (sess.last || 0)) > SESSION_MS) {
      sess = { id: uuid() };
      isNewSession = true;
    }
    sess.last = now;
    lsSet(SESS_KEY, JSON.stringify(sess));
    return sess.id;
  }

  // ── current-page marketing signals ──
  function currentAttribution() {
    var q;
    try { q = new URLSearchParams(location.search); } catch (e) { q = { get: function () { return null; } }; }
    var ref = document.referrer || "";
    return {
      utm_source: q.get("utm_source"),
      utm_medium: q.get("utm_medium"),
      utm_campaign: q.get("utm_campaign"),
      utm_term: q.get("utm_term"),
      utm_content: q.get("utm_content"),
      gclid: q.get("gclid"),
      fbclid: q.get("fbclid"),
      ttclid: q.get("ttclid"),
      referrer: ref,
      referrer_host: refHost(ref),
      landing_page: location.href,
      path: location.pathname,
      ts: new Date().toISOString()
    };
  }

  // Does this pageview carry a real acquisition signal (vs. internal navigation)?
  function hasSourceSignal(a) {
    if (a.utm_source || a.gclid || a.fbclid || a.ttclid) return true;
    if (a.referrer_host && !ownHost(a.referrer_host)) return true;
    return false;
  }

  function readSnap(key) {
    var raw = lsGet(key);
    if (!raw) return null;
    try { return JSON.parse(raw); } catch (e) { return null; }
  }

  var vid = getVisitorId();
  var sid = getSessionId();
  var cur = currentAttribution();
  var hasSignal = hasSourceSignal(cur);

  // FIRST touch — set once, on the visitor's very first page, and never overwrite.
  var ft = readSnap(FT_KEY);
  if (!ft) { ft = cur; lsSet(FT_KEY, JSON.stringify(ft)); }

  // LAST touch — refresh whenever a new session arrives with a real source signal
  // (don't let internal "direct" navigations overwrite a real last-touch).
  var lt = readSnap(LT_KEY);
  if (!lt || (isNewSession && hasSignal)) { lt = cur; lsSet(LT_KEY, JSON.stringify(lt)); }

  // ── beacon this pageview to the edge function ──
  function beacon() {
    var payload = {
      visitor_id: vid,
      session_id: sid,
      event_type: isNewSession ? "session_start" : "pageview",
      is_first_touch: isNewVisitor,
      utm_source: cur.utm_source,
      utm_medium: cur.utm_medium,
      utm_campaign: cur.utm_campaign,
      utm_term: cur.utm_term,
      utm_content: cur.utm_content,
      gclid: cur.gclid,
      fbclid: cur.fbclid,
      ttclid: cur.ttclid,
      referrer: cur.referrer,
      referrer_host: cur.referrer_host,
      landing_page: cur.landing_page,
      path: cur.path,
      shop: (window.Shopify && window.Shopify.shop) || null,
      template: (window.Theme && window.Theme.template && window.Theme.template.name) ||
        (document.querySelector("main[data-template]") &&
         document.querySelector("main[data-template]").getAttribute("data-template")) || null,
      user_agent: navigator.userAgent
    };
    var body = JSON.stringify(payload);
    // sendBeacon with a text/plain Blob is a CORS "simple request" (no preflight).
    try {
      if (navigator.sendBeacon) {
        var blob = new Blob([body], { type: "text/plain;charset=UTF-8" });
        if (navigator.sendBeacon(TRACK_URL, blob)) return;
      }
    } catch (e) {}
    try {
      fetch(TRACK_URL, { method: "POST", body: body, keepalive: true, mode: "no-cors",
        headers: { "Content-Type": "text/plain" } });
    } catch (e) {}
  }

  // ── public API: forms read attribution from here ──
  function snapFields(prefix, snap) {
    var out = {};
    snap = snap || {};
    out[prefix + "_source"] = snap.utm_source || null;
    out[prefix + "_medium"] = snap.utm_medium || null;
    out[prefix + "_campaign"] = snap.utm_campaign || null;
    out[prefix + "_referrer"] = snap.referrer || null;
    out[prefix + "_landing_page"] = snap.landing_page || null;
    return out;
  }

  window.InochiTracker = {
    getVisitorId: function () { return vid; },
    getSessionId: function () { return sid; },
    // Flat object intended to be spread straight into a form submission payload.
    getAttribution: function () {
      var a = { visitor_id: vid, session_id: sid };
      var f = snapFields("first_touch", ft);
      var l = snapFields("last_touch", lt);
      for (var k in f) a[k] = f[k];
      for (var k2 in l) a[k2] = l[k2];
      return a;
    }
  };

  beacon();
})();
